/**
 * The stacked-lookbook motion: the fixed tilt each cover lies at in the stack, and
 * the two flights between the stack and the grid. Both flights measure where the
 * covers are, change the layout, then play each cover from its old place to its
 * new one, so the grid itself stays ordinary CSS.
 */

/** GSAP's power3.inOut, the curve the stacked gallery this follows moves on. */
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';
const EXPAND_DURATION_MS = 700;
const EXPAND_STAGGER_MS = 250;
const COLLAPSE_DURATION_MS = 550;
const COLLAPSE_STAGGER_MS = 200;
/** A cover lies at up to this many degrees either side of straight. */
const MAX_TILT_DEG = 9;
/** The label under a tile arrives once its cover has mostly landed. */
const LABEL_DELAY_MS = 450;
const LABEL_DURATION_MS = 300;

export interface CardPlacement {
    centerX: number;
    centerY: number;
    /** Untransformed width, so a tilted card is not measured by its bounding box. */
    width: number;
}

export const prefersReducedMotion = (): boolean =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Decimal places a tilt keeps: the server and the browser must print the same number. */
const TILT_PRECISION = 2;

/** A stable pseudo-random tilt per position, so the stack looks the same every visit. */
export const stackRotation = (index: number): number => {
    const noise = 43758.5453 * Math.sin((index + 1) * 12.9898 + 78.233 * 1.1);
    const unit = noise - Math.floor(noise);
    return Number(((unit - 0.5) * 2 * MAX_TILT_DEG).toFixed(TILT_PRECISION));
};

export const measurePlacement = (element: HTMLElement): CardPlacement => {
    const rect = element.getBoundingClientRect();
    return {
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        width: element.offsetWidth,
    };
};

export const measureAll = (
    elements: (HTMLElement | null)[],
): (CardPlacement | undefined)[] =>
    elements.map((element) =>
        element ? measurePlacement(element) : undefined,
    );

/** Delay that starts a wave in the middle of the set and runs out to both ends. */
const fromCenter = (index: number, count: number, amount: number): number => {
    if (count <= 1) return 0;
    const middle = (count - 1) / 2;
    return (Math.abs(index - middle) / middle) * amount;
};

/** Delay that moves the last card first, so the top of the stack lands last. */
const fromEnd = (index: number, count: number, amount: number): number =>
    count <= 1 ? 0 : ((count - 1 - index) / (count - 1)) * amount;

/**
 * Plays each grid cover from the stack card it was. Covers past the end of the
 * stack had no card, so they grow out of the top card instead.
 */
export const flyCoversOutOfStack = (
    covers: (HTMLElement | null)[],
    stack: (CardPlacement | undefined)[],
): void => {
    const top = stack[0];
    if (!top || prefersReducedMotion()) return;

    covers.forEach((cover, index) => {
        if (!cover) return;
        const from = stack[index] ?? top;
        const to = measurePlacement(cover);
        const wasStacked = stack[index] !== undefined;
        const rotation = wasStacked ? stackRotation(index) : 0;
        const delay = fromCenter(index, covers.length, EXPAND_STAGGER_MS);

        cover.style.zIndex = String(covers.length - index);
        const flight = cover.animate(
            [
                {
                    transform: `translate(${from.centerX - to.centerX}px, ${from.centerY - to.centerY}px) rotate(${rotation}deg) scale(${from.width / to.width})`,
                    opacity: wasStacked ? 1 : 0,
                },
                { transform: 'none', opacity: 1 },
            ],
            {
                duration: EXPAND_DURATION_MS,
                easing: EASE_IN_OUT,
                delay,
                fill: 'backwards',
            },
        );
        // Cleared on cancel as well as on finish: a flight interrupted by a
        // collapse mid-air, or by leaving the page, would otherwise leave its
        // lift behind and hold the wrong cover above the rest.
        const dropLift = () => cover.style.removeProperty('z-index');
        flight.onfinish = dropLift;
        flight.oncancel = dropLift;

        cover.nextElementSibling?.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: LABEL_DURATION_MS,
            delay: delay + LABEL_DELAY_MS,
            fill: 'backwards',
        });
    });
};

/** Plays each stack card from the grid cover it was, back to its tilt. */
export const settleCardsIntoStack = (
    cards: (HTMLElement | null)[],
    grid: (CardPlacement | undefined)[],
): void => {
    if (prefersReducedMotion()) return;

    cards.forEach((card, index) => {
        const from = grid[index];
        if (!card || !from) return;
        const to = measurePlacement(card);

        card.animate(
            [
                {
                    transform: `translate(-50%, -50%) translate(${from.centerX - to.centerX}px, ${from.centerY - to.centerY}px) rotate(0deg) scale(${from.width / to.width})`,
                },
                {
                    transform: `translate(-50%, -50%) rotate(${stackRotation(index)}deg)`,
                },
            ],
            {
                duration: COLLAPSE_DURATION_MS,
                easing: EASE_IN_OUT,
                delay: fromEnd(index, cards.length, COLLAPSE_STAGGER_MS),
                fill: 'backwards',
            },
        );
    });
};
